"use client";

import { useCallback, useState } from "react";
import Image from "next/image";
import Turnstile from "@/components/shared/Turnstile";

export default function JoinMembership({
  title,
  subtitle,
  labels,
  backgroundSrc,
  locale = "fr",
}: {
  title: string;
  subtitle: string;
  labels: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    message: string;
    submit: string;
    success: string;
  };
  backgroundSrc: string;
  locale?: "fr" | "en";
}) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const onToken = useCallback((token: string) => setTurnstileToken(token), []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(false);
    try {
      const res = await fetch("/api/membership", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          tier: "private",
          locale,
          source: "home",
          turnstileToken,
        }),
      });
      if (!res.ok) throw new Error("request_failed");
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-white/25 pb-2 text-sm text-white placeholder-transparent focus:outline-none focus:border-white/60 transition-colors duration-200 font-body";

  const fieldClass = "flex flex-col gap-1";

  const labelClass = "text-xs text-white/50 font-body";

  return (
    <section className="relative overflow-hidden min-h-[520px] flex items-center">
      {/* Background image */}
      <Image
        src={backgroundSrc}
        alt="Join Welconcept"
        fill
        className="object-cover object-center"
        sizes="100vw"
        priority={false}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/72" />

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className="font-heading text-5xl md:text-6xl text-white font-light italic">
            {title}
          </h2>
          <p className="mt-4 text-sm text-white/55 font-body max-w-md mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Success state */}
        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <div className="w-10 h-px bg-white/40" />
            <p className="font-heading text-3xl text-white font-light italic">
              {labels.success}
            </p>
          </div>
        ) : (
        /* Form */
        <form onSubmit={handleSubmit} className="flex flex-col gap-8">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className={fieldClass}>
              <label className={labelClass}>{labels.firstName}</label>
              <input
                name="firstName"
                type="text"
                value={form.firstName}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div className={fieldClass}>
              <label className={labelClass}>{labels.lastName}</label>
              <input
                name="lastName"
                type="text"
                value={form.lastName}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className={fieldClass}>
              <label className={labelClass}>{labels.phone}</label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div className={fieldClass}>
              <label className={labelClass}>{labels.email}</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          {/* Row 3 — Message */}
          <div className={fieldClass}>
            <label className={labelClass}>{labels.message}</label>
            <input
              name="message"
              type="text"
              value={form.message}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          {/* Turnstile (rendu uniquement si la clé publique est définie) */}
          <Turnstile onToken={onToken} />

          {error && (
            <p className="text-center text-xs text-red-300/80 font-body">
              {locale === "fr"
                ? "Une erreur est survenue. Merci de réessayer."
                : "Something went wrong. Please try again."}
            </p>
          )}

          {/* Submit */}
          <div className="flex justify-center mt-2">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-white text-black text-xs tracking-[0.2em] uppercase font-body font-medium px-14 py-3.5 hover:bg-white/90 transition-colors duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? locale === "fr"
                  ? "Envoi…"
                  : "Sending…"
                : labels.submit}
            </button>
          </div>
        </form>
        )}
      </div>
    </section>
  );
}
